import React, { useState, useRef } from 'react';
import {
  Container,
  Typography,
  Paper,
  useMediaQuery,
  TextField,
  Box,
  Button,
} from '@mui/material';
import Header1 from '../Header/Header1';
import Footer2 from '../Footer/Footer2';

const salesPartnerAgreementText = `
SALES PARTNER AGREEMENT (COMPANY/BUSINESS ENTITY)

This Sales Partner Agreement ("Agreement") is made and entered into by and between:

Anthrasync Solutions Private Limited, a company incorporated under the laws of India and having its registered office at No 26, Laskar Hosur Road, Adugodi, Bangalore South, Bengaluru, Karnataka 560030, India, hereinafter referred to as the “Company” or “Platform” (which expression shall, unless repugnant to the context or meaning thereof, be deemed to include its successors and assigns),

AND

A company/entity registered under the laws of India, which wants to become a Sales Partner and is duly authorized to enter into this Agreement, hereinafter referred to as the “Sales Partner” (which expression shall, unless repugnant to the context or meaning thereof, be deemed to include its successors and assigns),

Collectively referred to as the “Parties”, and individually as a “Party”.

1. PURPOSE
The Company operates an AI-powered, real-time knowledge exchange platform where experts conduct live knowledge-sharing sessions. This Agreement outlines the terms under which the Sales Partner will promote the platform and earn commissions for successful paid bookings using their unique referral tools.

2. APPOINTMENT
The Company hereby appoints the Sales Partner as a non-exclusive, independent sales partner to market and promote its services in India. The Sales Partner shall use reasonable efforts to promote and refer users to book sessions through the platform.

3. ELIGIBILITY
To be eligible and maintain eligibility in the Sales Partner Program as a company or business entity, the applicant must:
Be a legally registered entity under Indian laws (e.g., Private Limited, LLP, Partnership, Sole Proprietorship).

Be represented by an authorized signatory with the legal power to bind the company to this Agreement.

Provide a valid PAN and, if applicable, a GST registration number.

Maintain a valid Indian business bank account for commission payments.

Have access to official email and phone communication channels.

Have operational capability to use the internet and the Partner Dashboard provided by the Company.

Have no pending criminal proceedings or past convictions against the business or its authorized representatives.

Commit to ethical marketing, accurate representation of services, and non-deceptive sales practices.
The Company reserves the right to request documents for verification or terminate the agreement for non-compliance.

4. RESPONSIBILITIES OF SALES PARTNER
The Sales Partner shall:
Promote the Company’s platform to potential users and organizations.

Use only approved marketing material provided or approved by the Company.

Not misrepresent or exaggerate the benefits or nature of the platform.

Use their unique coupon code or referral link provided by the Company.

Track their performance through the provided dashboard.

Immediately inform the Company of any misuse, suspicious activity, or technical issues.

Not engage in spamming, misleading advertisements, or unethical promotions.

5. COUPON CODE AND DASHBOARD ACCESS
The Company will issue a unique referral coupon code or link to each Sales Partner.

The Sales Partner will be given access to a Partner Dashboard to track:

Referrals/bookings

Commissions earned

Coupon usage and performance

The Company may update or modify the dashboard at its discretion.

6. COMMISSION AND PAYMENT TERMS
The Sales Partner shall receive a 10% commission on the value of each successful paid booking made using their referral code or link.

Bookings must be confirmed and paid for (not canceled or refunded) for commissions to be valid.

Payments will be processed monthly, on or before the 10th of each month for the previous month’s earnings.

Minimum payout threshold: ₹1,000. Earnings below this will roll over to the next month.

Commissions will be paid via bank transfer or any other mutually agreed method.

All applicable TDS and statutory deductions will be applied as per Indian tax law.

7. TERM AND TERMINATION
This Agreement is effective from the Effective Date and shall continue until terminated by either Party.

Either Party may terminate the Agreement with 15 days’ written notice.

The Company reserves the right to terminate this Agreement immediately for:

Breach of terms or conditions

Unethical or fraudulent behavior

Misuse of brand or platform

Inactivity for more than 90 consecutive days

8. INDEPENDENT CONTRACTOR STATUS
The Sales Partner is an independent contractor. Nothing in this Agreement creates an employment, partnership, or joint venture relationship.

The Sales Partner has no authority to bind or represent the Company in any manner.

9. INTELLECTUAL PROPERTY
All brand assets, content, materials, and intellectual property provided by the Company remain the sole property of the Company.

The Sales Partner may use approved materials only for promotional purposes and must not alter, misrepresent, or misuse them.

10. CONFIDENTIALITY
All information shared by the Company including customer data, marketing strategies, pricing, and technology shall be treated as confidential.

The Sales Partner shall not disclose any such information to third parties without prior written consent.

11. LIMITATION OF LIABILITY
The Company shall not be liable for any indirect, incidental, or consequential damages.

The Sales Partner agrees to indemnify and hold harmless the Company against any claims arising from their promotional activities or breach of this Agreement.

12. DEFAMATION
The Sales Partner agrees not to make, publish, or circulate any false, malicious, or disparaging statements—whether oral, written, or online—about the Company, its platform, employees, partners, or its services.

Any defamatory conduct, including posting misleading content on social media, forums, or public platforms, shall be deemed a material breach of this Agreement.

The Company reserves the right to initiate legal action, including seeking damages, in the event of defamation.

13. GOVERNING LAW AND JURISDICTION
This Agreement shall be governed by and construed in accordance with the laws of India. Any disputes arising out of or in connection with this Agreement shall be subject to the exclusive jurisdiction of the courts of Bengaluru, Karnataka, India.

14. DISPUTE RESOLUTION & FINAL DECISION
In the event of any dispute, confusion, or disagreement arising out of or in connection with the interpretation, performance, or execution of this Agreement, the Parties agree to attempt to resolve such disputes amicably through discussion and mutual understanding.
However, in matters solely related to:
Interpretation of this Agreement,

Referral or commission disputes,

Eligibility concerns, or

Sales Partner conduct and ethics,

the Company’s decision shall be final and binding on the Sales Partner.
This clause shall not restrict the Sales Partner's legal rights to pursue remedies available under applicable law, but the Parties agree to prioritize amicable resolution and accept the Company’s internal resolution in good faith.

15. ENTIRE AGREEMENT
This Agreement represents the entire understanding between the Parties and supersedes all prior agreements or understandings, oral or written, regarding the subject matter.

I confirm that I am duly authorized to enter into this Agreement on behalf of the company/entity.
`;

const SalesPartnerAgreementCompany = () => {
  const isMobile = useMediaQuery('(max-width:600px)');
  const [searchTerm, setSearchTerm] = useState('');
  const paperRef = useRef(null);

  const handleSearch = () => {
    if (!searchTerm.trim()) return;

    const element = paperRef.current;
    const regex = new RegExp(searchTerm, 'i');
    const match = salesPartnerAgreementText.match(regex);

    if (match && element) {
      const index = salesPartnerAgreementText.search(regex);
      const beforeMatch = salesPartnerAgreementText.slice(0, index);
      const lines = beforeMatch.split('\n');
      const targetLine = lines.length - 1;
      const lineHeight = 24;
      const scrollTop = targetLine * lineHeight;

      element.scrollTo({ top: scrollTop, behavior: 'smooth' });
    }
  };

  const getHighlightedText = (text, highlight) => {
    if (!highlight.trim()) return text;
    const parts = text.split(new RegExp(`(${highlight})`, 'gi'));
    return parts.map((part, i) =>
      part.toLowerCase() === highlight.toLowerCase() ? (
        <mark key={i} style={{ backgroundColor: '#ffe600', color: '#000' }}>
          {part}
        </mark>
      ) : (
        part
      )
    );
  };

  return (
    <div style={{ backgroundColor: '#000', minHeight: '100vh' }}>
      <Header1 />

      <Container maxWidth="md" sx={{ mt: 5, mb: 5 }}>
        <Typography
          variant={isMobile ? 'h5' : 'h4'}
          gutterBottom
          sx={{
            fontWeight: 600,
            textAlign: 'center',
            fontFamily: 'Verdana, sans-serif',
            color: '#ffffff',
          }}
        >
          Sales Partner Agreement (Company/Business Entity)
        </Typography>

        <Box
          display="flex"
          flexDirection={isMobile ? 'column' : 'row'}
          alignItems={isMobile ? 'stretch' : 'center'}
          gap={2}
          mb={2}
        >
          <TextField
            label="Search"
            variant="outlined"
            fullWidth
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{
              backgroundColor: '#1a1a1a',
              input: { color: '#fff' },
              label: { color: '#ffffff' },
              '& .MuiOutlinedInput-root': {
                '& fieldset': { borderColor: '#555' },
                '&:hover fieldset': { borderColor: '#888' },
                '&.Mui-focused fieldset': { borderColor: '#fff' },
              },
            }}
          />
          <Button
            variant="contained"
            onClick={handleSearch}
            sx={{
              fontFamily: 'Verdana, sans-serif',
              bgcolor: '#333',
              color: '#fff',
              '&:hover': { bgcolor: '#555' },
            }}
          >
            Search
          </Button>
        </Box>

        <Paper
          ref={paperRef}
          elevation={4}
          sx={{
            p: { xs: 2, sm: 3 },
            maxHeight: '75vh',
            overflowY: 'auto',
            whiteSpace: 'pre-line',
            backgroundColor: '#080808',
            color: '#e0e0e0',
            borderRadius: 2,
            fontFamily: 'Verdana, sans-serif',
            lineHeight: 1.6,
            boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.5)',
            scrollbarWidth: 'thin',
            '&::-webkit-scrollbar': {
              width: '6px',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: '#555',
              borderRadius: '3px',
            },
          }}
        >
          <Typography variant="body2" component="div" sx={{ fontFamily: 'Verdana, sans-serif' }}>
            {getHighlightedText(salesPartnerAgreementText, searchTerm)}
          </Typography>
        </Paper>
      </Container>

      <Footer2 />
    </div>
  );
};

export default SalesPartnerAgreementCompany;
